import { Box, IconButton, Modal, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import closeIcon from '../../../assets/MyAccountIcon/close-icon.svg';
import './PasswordManagerModal.css';
import { useState } from 'react';
import { axiosInstance } from '../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../../../i18n/LanguageContext';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  borderRadius: '8px',
  boxShadow: 24,
};

const PasswordManagerModal = ({ openPasswordManagerModal, setOpenPasswordManagerModal }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const { translate } = useTranslation();

  const togglePasswordVisibility = field => {
    switch (field) {
      case 'current':
        setShowPassword(!showPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  const handleClose = () => {
    setOpenPasswordManagerModal(false);
  };

  const handleUserNewPasswordChange = e => {
    setNewPassword(e.target.value);
    setPasswordError(null);
  };

  const handleUserConfirmNewPasswordChange = e => {
    setConfirmNewPassword(e.target.value);
    setPasswordError(null);
  };

  const password_validate = password => {
    const re = {
      capital: /(?=.*[A-Z])/,
      length: /.{8,40}/,
      specialChar: /[ -/:-@[-`{-~]/,
      digit: /(?=.*[0-9])/,
    };

    if (!re.length.test(password)) {
      return `${translate('resetPassword', 'passwordLength')}`;
    }
    if (!re.capital.test(password)) {
      return `${translate('resetPassword', 'passwordUppercase')}`;
    }
    if (!re.specialChar.test(password)) {
      return `${translate('resetPassword', 'passwordSpecial')}`;
    }
    if (!re.digit.test(password)) {
      return `${translate('resetPassword', 'passwordDigit')}`;
    }

    return null;
  };

  const handleSave = async e => {
    e.preventDefault();

    const passwordValidationError = password_validate(newPassword);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error(`${translate('resetPassword', 'passwordsNotMatch')}`);
      return;
    }

    try {
      const newData = {
        current_password: currentPassword,
        new_password: newPassword,
      };
      const res = await axiosInstance.post(`/auth/change-password`, newData);
      if (res?.status === 200) {
        toast.success(`${translate('myAccount', 'passwordResetMessageText')}`);
        setOpenPasswordManagerModal(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal
      open={openPasswordManagerModal}
      // onClose={handleClose}
    >
      <Box sx={style}>
        <Box className="password-modal-container">
          <Box className="password-modal-header">
            <Typography className="modal-title">
              {translate('myAccount', 'changePasswordText')}
            </Typography>
            <IconButton onClick={handleClose}>
              <img src={closeIcon || '/placeholder.svg'} alt="close" />
            </IconButton>
          </Box>

          <Box className="password-modal-body">
            <div className="password-form-group">
              <label className="password-label">
                {translate('myAccount', 'currentPasswordText')}
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="password-input"
                  placeholder={translate('myAccount', 'currentPasswordPlaceholderText')}
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                />
                <button
                  onClick={() => togglePasswordVisibility('current')}
                  className="visibility-toggle"
                >
                  {showPassword ? (
                    <VisibilityOff style={{ color: '#949DA6' }} />
                  ) : (
                    <Visibility style={{ color: '#949DA6' }} />
                  )}
                </button>
              </div>
            </div>

            <div className="password-form-group">
              <label className="password-label">{translate('myAccount', 'newPasswordText')}</label>
              <div className="password-input-wrapper">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  className="password-input"
                  placeholder={translate('myAccount', 'newPasswordPlaceholderText')}
                  value={newPassword}
                  onChange={handleUserNewPasswordChange}
                />
                <button
                  onClick={() => togglePasswordVisibility('new')}
                  className="visibility-toggle"
                >
                  {showNewPassword ? (
                    <VisibilityOff style={{ color: '#949DA6' }} />
                  ) : (
                    <Visibility style={{ color: '#949DA6' }} />
                  )}
                </button>
              </div>
              {passwordError && (
                <div style={{ color: 'red', fontSize: '13px' }}>{passwordError}</div>
              )}
            </div>

            <div className="password-form-group">
              <label className="password-label">
                {translate('myAccount', 'confirmPasswordText')}
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="password-input"
                  placeholder={translate('myAccount', 'confirmPasswordPlaceholderText')}
                  value={confirmNewPassword}
                  onChange={handleUserConfirmNewPasswordChange}
                />
                <button
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="visibility-toggle"
                >
                  {showConfirmPassword ? (
                    <VisibilityOff style={{ color: '#949DA6' }} />
                  ) : (
                    <Visibility style={{ color: '#949DA6' }} />
                  )}
                </button>
              </div>
              {passwordError && (
                <div style={{ color: 'red', fontSize: '13px' }}>{passwordError}</div>
              )}
            </div>
          </Box>

          <Box className="password-modal-footer">
            <button className="cancel-button" onClick={handleClose}>
              {translate('myAccount', 'cancelButtonText')}
            </button>
            <button className="save-button" onClick={handleSave}>
              {translate('myAccount', 'changePasswordButtonText')}
            </button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PasswordManagerModal;
